import React, {
  useRef,
  useMemo,
  useState,
} from 'react';

import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Icon,
  Tooltip,
  Dropdown,
  TextInput,
} from 'web-components';
import {
  getUiState,
} from 'web-selectors';
import { uiActions } from 'web-actions';

import {
  numberMask,
  getPropertyValue,
  getUnitByUnitSystem,
  getUnitsByUnitSystem,
  getConvertedUnitName,
  convertPropertyRawValue,
} from 'web-utils';

import Units from './Units';
import * as S from './styled';

const getDefaultMin = (secs: number) => {
  const mind = secs % (60 * 60);
  return Math.floor(mind / 60);
};

const getDefaultSec = (secs: number) => {
  const mind = secs % (60 * 60);
  return Math.ceil(mind % 60);
};

const getInitialProperty = (initialValue: any, initialUnit: string) => {
  const property = initialValue?.personal_property || {};
  property.selectedUnit = property.property_units?.find(
    (u: any) => u.short_name === initialUnit,
  );

  return property;
};

const mapState = state => ({
  settings: coreSelectors.getProfile('user_settings')(state),
  properties: coreSelectors.getVisiblePersonalProperties(state),
  ...getUiState([
    'openedTooltips',
  ])(state),
});

type Props = {
  initialValue?: any;
  onSubmit?: { (data: any): void };
  onChange?: { (data: any): void };
  onDelete: { (): void };
};

const EditProperty = ({
  initialValue = {},
  onSubmit,
  onChange,
  onDelete,
}: Props) => {
  const dispatch = useDispatch();
  const inputEl = useRef<any>(null);
  const secInputEl = useRef<HTMLInputElement>(null);
  const { settings, properties, openedTooltips } = useSelector(mapState, shallowEqual);

  const initialUnit = getConvertedUnitName(initialValue.property_unit_name, settings.units_system);
  const [value, setValue] = useState(
    getPropertyValue(
      initialValue,
      initialUnit,
    ),
  );
  const [property, setProperty] = useState(getInitialProperty(initialValue, initialUnit));
  const unit = property.selectedUnit || property.default_unit;
  const unitName = unit?.short_name;

  const handleSubmit = (ev: any) => {
    if (!unit) return;
    ev.preventDefault();
    ev.stopPropagation();
    /* For a group property it doesn't exist */
    onSubmit?.({
      property_unit_id: unit.id,
      personal_property_id: property.id,
      ...convertPropertyRawValue(
        value,
        unit.short_name,
        property.save_unit?.short_name || property.default_unit.short_name,
      ),
    });

    if (openedTooltips.some(el => el.key === 'exercise_property')) {
      dispatch(
        uiActions.setUiState({
          openedTooltips: openedTooltips.filter(el => el.key !== 'exercise_property'),
        }),
      );

      dispatch(
        coreActions.requestMarkTutotialStepsComplite({
          steps: ['build_your_own_workout'],
        }),
      );
    }
  };

  const inputProps = useMemo(() => ({
    min: () => ({
      style: {
        width: '22px',
        paddingRight: 0,
        textAlign: 'center',
      },
      type: 'number',
      maxLength: '2',
      placeholder: 'M',
      defaultValue: getDefaultMin(value) || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const mins = parseInt(e.target.value || '0', 10);
        const secs = parseInt(secInputEl.current?.value || '0', 10);
        const newValue = (mins * 60) + secs;
        setValue(newValue);
        onChange?.({ value: newValue });
      },
    }),
    sec: () => ({
      style: {
        width: '22px',
        paddingLeft: 0,
        textAlign: 'center',
      },
      type: 'number',
      maxLength: '2',
      placeholder: 'S',
      defaultValue: getDefaultSec(value) || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const mins = parseInt(inputEl.current?.value || '0', 10);
        const secs = parseInt(e.target.value || '0', 10);
        const newValue = (mins * 60) + secs;
        setValue(newValue);
        onChange?.({ value: newValue });
      },
    }),
    rest: () => ({
      mask: numberMask,
      maxLength: '8',
      placeholder: 'value',
      defaultValue: value || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);

        onChange?.(
          convertPropertyRawValue(
            newValue,
            unit.short_name,
            property.save_unit?.short_name || property.default_unit.short_name,
          ),
        );
      },
    }),
  }), [unitName]);

  const isTime = (unitName === 'mmss' || unitName === 's');

  const inputWrapper = (
    <S.InputWrapper>
      {property.name === 'Distance' && (
        <Units
          units={getUnitsByUnitSystem(property.property_units, settings.units_system)}
          activeId={unit.id}
          onChange={(selectedUnit: any) => {
            setProperty({
              ...property,
              selectedUnit,
            });
            onChange?.({
              property_unit_id: selectedUnit.id,
            });
          }}
        />
      )}
      <TextInput
        {...(isTime
          ? inputProps.min()
          : inputProps.rest()
        )}
        inputRef={inputEl}
      />
      {isTime && (
        <>
          :
          <TextInput
            {...inputProps.sec()}
            inputRef={secInputEl}
          />
        </>
      )}
    </S.InputWrapper>
  );

  return (
    <S.Form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleSubmit(e);
        }
      }}
    >
      <Dropdown
        positions={['top']}
        trigger={(
          <S.Btn
            type="button"
          >
            {property?.name || 'Property'}
            {' '}
            <Icon type="fas fa-sort" />
          </S.Btn>
        )}
        items={properties}
        onChange={(p) => {
          const selectedUnit = getUnitByUnitSystem(p, settings.units_system);

          setProperty({
            ...p,
            selectedUnit,
          });
          onChange?.({
            property_unit_id: selectedUnit.id,
            personal_property_id: p.id,
          });
          setValue('');
          setTimeout(() => {
            if (inputEl.current?.focus) {
              inputEl.current.focus();
            }

            if (inputEl.current?.inputElement?.focus) {
              inputEl.current.inputElement.focus();
            }
          }, 100);
        }}
      />

      {initialValue.isNew && openedTooltips.some(el => el.key === 'exercise_property') ? (
        <Tooltip
          body="Edit exercise metrics here, and add more using the + button."
          toggleMode
          initialState
        >
          {inputWrapper}
        </Tooltip>
      ) : inputWrapper}

      {onSubmit && (
        <S.Btn
          type="button"
          onClick={handleSubmit}
        >
          <Icon
            type="fas fa-check"
            color="success"
          />
        </S.Btn>
      )}
      <S.Btn
        type="button"
        onClick={onDelete}
      >
        <Icon
          type="fas fa-times"
          color="danger"
        />
      </S.Btn>
    </S.Form>
  );
};

export default EditProperty;
